/* eslint-disable @typescript-eslint/ban-types */
import { useState, useRef, useEffect } from 'react';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { IconButton, TextField, Typography } from '@mui/material';
import {
  CloseOutlined as CloseOutlinedIcon,
  SmartDisplayOutlined as SmartDisplayOutlinedIcon,
  CropOriginalOutlined as CropOriginalOutlinedIcon,
} from '@mui/icons-material';
import ReactHtmlParser from 'react-html-parser';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { FONT_SIZES, PALETTE } from '~/assets/styles/base';
import { ORIGINAL_FORM_VALIDATION, QUESTION_TYPE_ID } from '~/common/constants';
import { getOriginalFormsValidationMsg, removeTagsOnQuestionLabel } from '~/common/functional';
import { QuestionProps } from '~/common/types';
import InputValidationErrorTooltip from '~/components/ui-parts/InputValidationErrorTooltip';
import ComponentControl from '../ComponentControl';
import ImageUploadModal from '../ImageUploadModal';
import VideoUploadModal from '../VideoUploadModal';
import { LOCALE_STRING } from '~/constants';

type ContentBoxProps = {
  $isActive?: boolean;
  $isBoxFormValid?: boolean;
};
const ContentBox = styled.div<ContentBoxProps>`
  width: 768px;
  padding: 25px;
  margin-top: 20px;
  border: 1px solid ${(props) => (props.$isActive ? PALETTE.primary.main : PALETTE.border.frame)};
  background: ${PALETTE.white};
  display: flex;
  flex-direction: column;

  ${(props) => {
    if (!props.$isBoxFormValid) {
      return `
        border: 1px solid ${PALETTE.error.main};
      `;
    }
    if (props.$isActive) {
      return `
      border: 1px solid ${PALETTE.primary.main};
      `;
    }
    return `
      border: 1px solid ${PALETTE.border.frame};
    `;
  }}
`;

type PageNumInfoBoxProps = {
  $isActive?: boolean;
  $isBoxFormValid?: boolean;
};
const PageNumInfoBox = styled.div<PageNumInfoBoxProps>`
  background: ${(props) => (props.$isActive ? PALETTE.primary.subButton : PALETTE.blue.main)};
  border: 1px solid ${(props) => (props.$isActive ? PALETTE.primary.main : PALETTE.border.frame)};
  height: 32px;
  width: 768px;
  margin: -26px 0 24px -26px;
  padding-left: 14px;
  padding-top: 6px;
  &:hover {
    cursor: move;
  }

  ${(props) => {
    if (!props.$isBoxFormValid) {
      return `
        border: 1px solid ${PALETTE.error.main};
        background: ${PALETTE.error.light};
      `;
    }
    if (props.$isActive) {
      return `
      border: 1px solid ${PALETTE.primary.main};
      background: ${PALETTE.primary.subButton};
      `;
    }
    return `
      border: 1px solid ${PALETTE.border.frame};
      background: ${PALETTE.blue.main};
    `;
  }}
`;

const SurveyInfoBoxNotes = styled(Typography)`
  font-size: ${FONT_SIZES.body3};
  color: ${PALETTE.text.sub};
  padding-top: 12px;
`;

const SurveyInfo = styled.div`
  padding: 12px 0 8px;
  display: inline-flex;
  justify-content: space-between;
`;

const StyledTextField = styled(TextField)`
  width: 600px;
  input[type='text'] {
    padding: 5px 0 5px 0px;
    font-size: ${FONT_SIZES.bodyDefault};
    color: ${PALETTE.text.main};
    border-bottom: 1px solid ${PALETTE.border.frame};
  }
`;

const ImgVideoPreviewContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 20px;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
`;

const StyledImg = styled.img`
  max-height: 170px;
`;

const ImageUploadArea = styled.div`
  margin-right: 10px;
  display: flex;
  flex-direction: row;
`;

type Props = {
  isActive?: boolean;
  currentSection: number;
  totalSection: number;
  handleContentChange: Function;
  handleDuplicateQuestion: Function;
  handleDeleteQuestion: Function;
  questionData: QuestionProps;
  itemIdx: number;
  handleIsActiveElement: Function;
  isValidationExecuted: boolean;
  id: string;
};

const DescriptionArea: React.FC<Props> = ({
  isActive,
  currentSection,
  totalSection,
  handleContentChange,
  itemIdx,
  questionData,
  handleDuplicateQuestion,
  handleDeleteQuestion,
  handleIsActiveElement,
  isValidationExecuted,
  id,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  const ref = useRef<HTMLDivElement>(null);

  const [questionContent, setQuestionContent] = useState<QuestionProps>(questionData);

  const [showImageUploadModal, setShowImageUploadModal] = useState<boolean>(false);
  const [showVideoUploadModal, setShowVideoUploadModal] = useState<boolean>(false);

  const [imgPreview, setImgPreview] = useState<JSX.Element[]>([]);
  const [videoPreview, setVideoPreview] = useState<JSX.Element | undefined>();
  const [videoUrl, setVideoUrl] = useState('');

  const [isBoxFormValid, setIsBoxFormValid] = useState(true);

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
  });

  const styleDnd: React.CSSProperties = {
    transform: CSS.Transform.toString(transform)?.replace(/scaleY\([^)]*\)\s*/, ''),
    transition,
  };

  const showInputError = (tmpIsValidate?: boolean, tmpIsBoxFormValid?: boolean) => {
    if (tmpIsValidate !== undefined && tmpIsValidate) {
      if (tmpIsBoxFormValid !== undefined) {
        return tmpIsBoxFormValid;
      }
    }
    return true;
  };

  const updateQuestionContent = (newLabel: string, onBlur = true) => {
    const newDescription = newLabel;
    // eslint-disable-next-line no-unused-vars
    const { singleAnswerConfig, ...newQuestionContent } = {
      ...questionContent,
      type: QUESTION_TYPE_ID.TEXT,
      required: false,
      label: newDescription,
      isQuestionValid: true,
    };

    setQuestionContent(newQuestionContent);
    handleContentChange(newQuestionContent, itemIdx, onBlur);
  };

  useEffect(() => {
    if (isActive) {
      handleIsActiveElement(itemIdx, ref);
    }
  }, [isActive]);

  const removeVideoTag = (dataWithTag: string) => {
    let currentLabel = dataWithTag;
    const iFrameTagRegex = /<iframe.*<\/iframe>/g;
    const newLabel = currentLabel.match(iFrameTagRegex);

    if (newLabel !== null) {
      currentLabel = currentLabel.replace(newLabel[0], '');
    }

    return currentLabel;
  };

  const removeImgTag = (dataWithTag: string) => {
    let currentLabel = dataWithTag;
    const imgTagRegex = /<img.*?\/>/g;
    const newLabels = currentLabel.match(imgTagRegex);
    if (newLabels !== null) {
      newLabels.forEach((newLabel) => {
        currentLabel = currentLabel.replace(newLabel, '');
      });
    }
    return currentLabel;
  };

  const getDescriptionValue = (labelValue: string) => {
    let newValue = removeVideoTag(labelValue);
    newValue = removeImgTag(newValue);
    return newValue;
  };

  const setImgVideoUrlToLabel = (
    label: string,
    previewVideoData?: JSX.Element,
    previewImgData?: JSX.Element[],
    onBlur = true
  ) => {
    let content = getDescriptionValue(label);
    if (previewVideoData) {
      content += renderToString(previewVideoData);
    }
    if (previewImgData && previewImgData.length > 0) {
      previewImgData.forEach((data) => {
        content += renderToString(data);
      });
    }

    updateQuestionContent(content, onBlur);
  };

  const handleImageUploadSetting = (file?: JSX.Element) => {
    setShowImageUploadModal(false);

    if (file) {
      // Get the current array of file previews
      const currentPreviews = [...imgPreview];

      if (currentPreviews.length >= ORIGINAL_FORM_VALIDATION.MAX_QUESTION_IMG) {
        // Maximum limit reached
        return;
      }

      // Add the newly uploaded file preview to the array
      const updatedPreview = [...currentPreviews, file];
      // Update the state variable with the new array of file previews
      setImgPreview(updatedPreview);
      setImgVideoUrlToLabel(questionContent.label, videoPreview, updatedPreview);
    }
  };

  const handleVideoUploadSetting = (urlValue: string, previewData?: JSX.Element) => {
    setShowVideoUploadModal(false);
    setVideoPreview(previewData);
    setVideoUrl(urlValue);
    setImgVideoUrlToLabel(questionContent.label, previewData, imgPreview);
  };

  const updateQuestionLabelWithoutVideo = () => {
    const currentLabel = removeVideoTag(questionContent.label);
    updateQuestionContent(currentLabel);
    setVideoUrl('');
    setVideoPreview(undefined);
  };

  const updateQuestionLabelWithoutImg = (index: number) => {
    const currentPreviews = [...imgPreview];
    // Remove the image preview at the specified index
    currentPreviews.splice(index, 1);
    // Update the state variable with the modified array of file previews
    setImgPreview(currentPreviews);
    // Update the label to contain only the files from current previews
    setImgVideoUrlToLabel(questionContent.label, videoPreview, currentPreviews);
  };

  useEffect(() => {
    const currentLabel = questionContent.label;

    const iFrameTagRegex = /<iframe.*<\/iframe>/g;
    let newLabel = currentLabel.match(iFrameTagRegex);

    if (newLabel !== null) {
      setVideoPreview(ReactHtmlParser(newLabel[0])[0]);
      setVideoUrl(ReactHtmlParser(newLabel[0])[0].props.src.replace('embed/', 'watch?v='));
    }

    const imgTagRegex = /<img.*\/>/g;
    newLabel = currentLabel.match(imgTagRegex);

    if (newLabel !== null) {
      setImgPreview(ReactHtmlParser(newLabel[0]));
    }
  }, []);

  useEffect(() => {
    if (!isValidationExecuted) {
      setIsBoxFormValid(true);
    } else {
      setIsBoxFormValid(questionData.isQuestionValid ?? true);
    }
  }, [questionData]);

  useEffect(() => {
    if (!isBoxFormValid) {
      handleIsActiveElement(itemIdx, ref, true);
    }
  }, [isBoxFormValid]);

  return (
    <div ref={setNodeRef}>
      <ContentBox
        onClick={() => handleIsActiveElement(itemIdx, ref)}
        data-testid="descriptionArea"
        $isActive={isActive}
        ref={ref}
        style={styleDnd}
        $isBoxFormValid={showInputError(isValidationExecuted, questionData.isQuestionValid)}
      >
        <PageNumInfoBox
          $isBoxFormValid={showInputError(isValidationExecuted, questionData.isQuestionValid)}
          $isActive={isActive}
          data-testid="descriptionArea-pageNumInfo"
          {...attributes}
          {...listeners}
        >
          <Typography sx={{ fontSize: 14 }}>
            {`${currentSection}/${totalSection} ${t(
              'registration.originalForm.manual.pageBreak.page'
            )}`}
          </Typography>
        </PageNumInfoBox>

        <SurveyInfoBoxNotes>
          {t('registration.originalForm.manual.surveyInfoBox.desc')}
        </SurveyInfoBoxNotes>

        <SurveyInfo>
          <InputValidationErrorTooltip
            error={!showInputError(isValidationExecuted, questionData.isQuestionValid)}
            message={t(
              getOriginalFormsValidationMsg(
                removeTagsOnQuestionLabel(questionContent.label),
                ORIGINAL_FORM_VALIDATION.QUESTION_LABEL_MAX_SIZE
              ),
              {
                maxLength: ORIGINAL_FORM_VALIDATION.QUESTION_LABEL_MAX_SIZE.toLocaleString(
                  LOCALE_STRING.ja
                ),
              }
            )}
          >
            <StyledTextField
              data-testid={`description-${itemIdx}`}
              variant="standard"
              fullWidth
              value={getDescriptionValue(questionContent.label)}
              onChange={(e) => {
                setImgVideoUrlToLabel(e.target.value, videoPreview, imgPreview, false);
              }}
              onBlur={(e) => {
                setImgVideoUrlToLabel(e.target.value, videoPreview, imgPreview);
              }}
              inputProps={{
                type: 'text',
                'data-testid': 'text-area-component',
              }}
              placeholder="ここに説明が入ります"
              error={!showInputError(isValidationExecuted, questionData.isQuestionValid)}
            />
          </InputValidationErrorTooltip>

          <ImageUploadArea>
            <IconButton
              onClick={() => {
                setShowVideoUploadModal(true);
              }}
              sx={{ marginTop: '-10px' }}
              aria-label="upload-url"
            >
              <SmartDisplayOutlinedIcon sx={{ fontSize: 26, color: '#425F77' }} />
            </IconButton>
            <IconButton
              sx={{ marginTop: '-10px' }}
              onClick={() => {
                setShowImageUploadModal(true);
              }}
              aria-label="upload-image"
            >
              <CropOriginalOutlinedIcon sx={{ fontSize: 26, color: '#425F77' }} />
            </IconButton>
          </ImageUploadArea>
        </SurveyInfo>

        {showImageUploadModal && (
          <ImageUploadModal
            isOpen={showImageUploadModal}
            handleClose={() => setShowImageUploadModal(false)}
            handleSetting={handleImageUploadSetting}
          />
        )}

        {showVideoUploadModal && (
          <VideoUploadModal
            isOpen={showVideoUploadModal}
            handleClose={() => setShowVideoUploadModal(false)}
            handleSetting={handleVideoUploadSetting}
            videoUrl={videoUrl}
          />
        )}

        {videoPreview && (
          <ImgVideoPreviewContainer data-testid="videoPreviewDiv">
            {videoPreview}
            <IconButton
              onClick={() => updateQuestionLabelWithoutVideo()}
              data-testid="deleteVideoButton"
            >
              <CloseOutlinedIcon style={{ fontWeight: 'bold', fontSize: '20px' }} />
            </IconButton>
          </ImgVideoPreviewContainer>
        )}

        {imgPreview.length > 0 &&
          imgPreview.map((preview, index) => (
            <ImgVideoPreviewContainer data-testid={`imgPreviewDiv-${index}`} key={index}>
              <StyledImg src={preview.props.src} alt={preview.props.alt} />
              <IconButton
                onClick={() => updateQuestionLabelWithoutImg(index)}
                data-testid={`deleteImgPreviewButton-${index}`}
              >
                <CloseOutlinedIcon style={{ fontWeight: 'bold', fontSize: '20px' }} />
              </IconButton>
            </ImgVideoPreviewContainer>
          ))}

        <ComponentControl
          handleDuplicateQuestion={handleDuplicateQuestion}
          handleDeleteQuestion={handleDeleteQuestion}
          itemIdx={itemIdx}
          questionType={QUESTION_TYPE_ID.TEXT}
        />
      </ContentBox>
    </div>
  );
};

export default DescriptionArea;
